import cloneDeep from 'lodash/cloneDeep';

import FunnelsPermalink from './funnels';
import InsightsPermalink from './insights';
import RetentionPermalink from './retention';

export function generateAnomalyPermalink({alert, projectId, selectedExplains, shared, workspaceId}, absolute = false) {
  const commonParams = {
    alertType: `ANOMALY`,
    alertId: alert.alertId,
    projectId,
    workspaceId,
  };
  if (selectedExplains) {
    commonParams.selectedExplains = selectedExplains;
  }
  if (shared) {
    commonParams.shared = shared;
  }
  if (alert.reportId) {
    // report id in the API, but it's really the bookmark id. Will be changed in our new API
    commonParams.bookmarkId = alert.reportId;
  }
  const {anomaly} = alert;
  const {funnelsQuery, insightsQuery, retentionQuery, anomalyDetail, anomalyDetails} = anomaly;
  const detail = anomalyDetail ? anomalyDetail : anomalyDetails[0];
  if (insightsQuery) {
    const clonedInsightsQuery = cloneDeep(insightsQuery);
    // TODO(mack): The backend should be returning the anomalyDetail with showAction UNIQUE as the first
    // one if there's both UNIQUE and TOTAL, but it's safer for the front-end to specifically look for the
    // UNIQUE one.
    // The insightsQuery will only have a single showClause for now for anomalies.
    const action = detail.insightsDetails.showAction;
    const anomalyTimestamp = detail.anomalyTimestamp;
    clonedInsightsQuery.showClauses[0].showEvent.action = action;
    return InsightsPermalink.generatePermalink(
      Object.assign(commonParams, {
        query: clonedInsightsQuery,
        alertTimestamp: anomalyTimestamp,
      }),
      absolute,
    );
  } else if (funnelsQuery) {
    return FunnelsPermalink.generatePermalink(
      Object.assign(commonParams, {
        query: funnelsQuery,
        startStepIdx: detail.funnelsDetails.startStepIdx,
      }),
      absolute,
    );
  } else if (retentionQuery) {
    return RetentionPermalink.generatePermalink(Object.assign(commonParams, {query: retentionQuery}), absolute);
  }
}

function getGroupByPropertyForContour(contour) {
  return {
    name: contour.property,
    source: `EVENTS`,
    type: contour.propertyType,
    typecast: `STRING`,
  };
}

function getPropertyFilterForContour(contour) {
  return {
    stringFilter: {
      operator: `SET`,
    },
    property: getGroupByPropertyForContour(contour),
  };
}

export function generateContourPermalink({alert, projectId, shared, workspaceId}, absolute = false) {
  const contour = alert.contour;
  const commonParams = {
    alertType: `CONTOUR`,
    alertId: alert.alertId,
    projectId,
    workspaceId,
  };
  if (shared) {
    commonParams.shared = shared;
  }

  if (contour.funnelsDetails) {
    const propertyFilters = (() => {
      if (contour.property === `campaign_id`) {
        // You cannot add a "is set" filter for campaign_id in funnels
        return [];
      } else {
        return [getPropertyFilterForContour(contour)];
      }
    })();

    return FunnelsPermalink.generatePermalink(
      Object.assign(commonParams, {
        query: {
          bookmarkId: contour.funnelsDetails.bookmarkId,
          groupByProperty: getGroupByPropertyForContour(contour),
          propertyFilters,
          dateRange: contour.dateRange,
          timeUnit: `DAY`,
        },
      }),
      absolute,
    );
  } else if (contour.retentionDetails) {
    const retentionDetails = contour.retentionDetails;
    const event = retentionDetails.event;
    const bornEvent = retentionDetails.bornEvent;
    const isCompounded = event === bornEvent;
    return RetentionPermalink.generatePermalink(
      Object.assign(commonParams, {
        query: {
          event,
          bornEvent: isCompounded ? null : bornEvent,
          segmentEvent: isCompounded ? event : bornEvent,
          segmentMethod: `ALL`,
          // The property will always be an event property (i.e. not a user property)
          segmentOn: `properties["${contour.property}"]`,
          countType: `PERCENT`,
          retentionType: isCompounded ? `COMPOUNDED` : `BIRTH`,
          dateRange: contour.dateRange,
          timeUnit: retentionDetails.timeUnit,
        },
      }),
      absolute,
    );
  }
}

export function generatePermalink({alert, projectId, selectedExplains, shared, workspaceId}, absolute = false) {
  if (alert.anomaly) {
    return generateAnomalyPermalink(
      {
        alert,
        projectId,
        selectedExplains,
        shared,
        workspaceId,
      },
      absolute,
    );
  } else if (alert.contour) {
    return generateContourPermalink(
      {
        alert,
        projectId,
        shared,
        workspaceId,
      },
      absolute,
    );
  }
}
